import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoClose } from "react-icons/io5";
import servicesData from "../data/servicesData.json";
import ImageGallery from "./ImageGallery";
import hk1 from "../assets/images/hk1.jpg";
import hk2 from "../assets/Service_img/hk2.jpg";
import hk3 from "../assets/Service_img/hk3.jpg";
import pc1 from "../assets/Service_img/pc1.jpg";
import pc2 from "../assets/Service_img/pc2.jpg";
import pc3 from "../assets/Service_img/pc3.jpg";
import lc1 from "../assets/Service_img/lc1.jpg";
import lc2 from "../assets/Service_img/lc2.jpg";
import lc3 from "../assets/Service_img/lc3.jpg";
import pa1 from "../assets/Service_img/pa1.jpg";
import pa2 from "../assets/Service_img/pa2.jpg";
import pa3 from "../assets/Service_img/pa3.jpg";
import fa1 from "../assets/Service_img/fa1.jpg";
import fa2 from "../assets/Service_img/fa2.jpg";
import fa3 from "../assets/Service_img/fa3.jpg";
import pl1 from "../assets/Service_img/pl1.jpg";
import pl2 from "../assets/Service_img/pl2.jpg";
import pl3 from "../assets/Service_img/pl3.jpg";
import el1 from "../assets/Service_img/el1.jpg";
import el2 from "../assets/Service_img/el2.jpg";
import el3 from "../assets/Service_img/el3.jpg";

const ServiceModal = ({ isOpen, onClose, serviceId }) => {
    const [serviceDetails, setServiceDetails] = useState(null);

    useEffect(() => {
        if (isOpen && serviceId) {
            const details = servicesData.services.find(
                (service) => service.id === serviceId
            );
            setServiceDetails(details);
        }
    }, [isOpen, serviceId]);

    const galleryImages = useMemo(() => {
        if (!serviceDetails) return [];
        switch (serviceDetails.id) {
            case 1:
                return [hk1, hk2, hk3];
            case 2:
                return [pc1, pc2, pc3];
            case 3:
                return [lc1, lc2, lc3];
            case 4:
                return [pa1, pa2, pa3];
            case 5:
                return [fa1, fa2, fa3];
            case 6:
                return [pl1, pl2, pl3];
            case 7:
                return [el1, el2, el3];
            default:
                return Array.isArray(serviceDetails.gallery)
                    ? serviceDetails.gallery
                    : [];
        }
    }, [serviceDetails]);

    if (!serviceDetails) return null;

    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Overlay */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="fixed inset-0 bg-black bg-opacity-50 z-40"
                    />

                    {/* Modal */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
                    >
                        <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-4 border-b">
                                <h2 className="text-2xl font-bold text-gray-800">
                                    {serviceDetails.title}
                                </h2>
                                <button
                                    onClick={onClose}
                                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                                >
                                    <IoClose size={24} />
                                </button>
                            </div>

                            {/* Modal Content */}
                            <div className="p-6">
                                {/* Image Gallery */}
                                <div className="mb-6">
                                    <ImageGallery images={galleryImages} />
                                </div>

                                {/* Description */}
                                <div className="space-y-4">
                                    <h3 className="text-xl font-semibold text-gray-800">
                                        Description
                                    </h3>
                                    <div className="text-gray-600 space-y-2">
                                        {serviceDetails.description.map(
                                            (paragraph, index) => (
                                                <p key={index}>{paragraph}</p>
                                            )
                                        )}
                                    </div>
                                </div>

                                {/* Features */}
                                {serviceDetails.features && (
                                    <div className="mt-6">
                                        <h3 className="text-xl font-semibold text-gray-800 mb-3">
                                            Features
                                        </h3>
                                        <ul className="list-disc list-inside text-gray-600 space-y-2">
                                            {serviceDetails.features.map(
                                                (feature, index) => (
                                                    <li key={index}>
                                                        {feature}
                                                    </li>
                                                )
                                            )}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ServiceModal;
